<script setup lang="ts">
import EmailTagsInput from '~/components/input/EmailTagsInput.vue'
import BillingLetterNextSteps from '~/ui-blocks/Formalization/blocks/paymentModel/supplierInformation/BillingLetterNextSteps.vue'
import BillingLetterSummary from '~/ui-blocks/Formalization/blocks/paymentModel/supplierInformation/BillingLetterSummary.vue'

const { downloadLink } = defineProps<{
  supplierName: string
  emails: string[]
  maxEmails: number
  emailError?: string
  locked: boolean
  sent: boolean
  downloadLink: string | null
  sending: boolean
  linkPending?: boolean
}>()

const emit = defineEmits<{
  'update:emails': [emails: string[]]
  'send': []
}>()

const { t } = useI18n()

const emailTagsInputRef = ref<InstanceType<typeof EmailTagsInput> | null>(null)

function downloadLetter() {
  if (!downloadLink)
    return
  window.open(downloadLink, '_blank', 'noopener,noreferrer')
}

function commitPending(): boolean {
  return emailTagsInputRef.value?.commitPending() ?? true
}

function focus() {
  emailTagsInputRef.value?.focus()
}

defineExpose({
  commitPending,
  focus,
})
</script>

<template>
  <section class="billing-letter" data-testid="billing-letter">
    <div class="billing-letter__header">
      <h4 class="billing-letter__title">
        {{ t('receipt-model.billing_letter.title') }}
      </h4>
      <!-- No <p> in this block: the accordion applies `-mx-4` to every `div p` below lg:system (Accordion.vue). -->
      <div v-if="!locked" class="billing-letter__description">
        <span class="block">{{ t('receipt-model.billing_letter.description') }}</span>
        <span class="block">{{ t('receipt-model.billing_letter.description_complement') }}</span>
      </div>
    </div>

    <BillingLetterSummary
      v-if="locked"
      :emails="emails"
      :download-link="downloadLink"
      @download="downloadLetter()"
    />

    <template v-else>
      <SolAlert
        v-if="sent"
        id="billing-letter-sent"
        class="billing-letter__sent-alert"
        feedback="success"
        :title="t('receipt-model.billing_letter.sent_alert.title')"
        :action="downloadLink ? t('receipt-model.billing_letter.download') : undefined"
        :aria-busy="linkPending || undefined"
        @click="downloadLetter()"
      >
        {{ t('receipt-model.billing_letter.sent_alert.text') }}
      </SolAlert>

      <div class="flex flex-col gap-nano sm:system:flex-row sm:system:items-start">
        <EmailTagsInput
          id="supplierEmail"
          ref="emailTagsInputRef"
          class="flex-1 min-w-0"
          name="supplierEmail"
          :model-value="emails"
          :max="maxEmails"
          :label="t('receipt-model.billing_letter.emails_label', { name: supplierName })"
          :error="emailError"
          @update:model-value="emit('update:emails', $event)"
        />
        <SolButton
          id="send-email-button"
          class="billing-letter__send-button !bg-neutral-high-pure !text-2xs sm:system:mt-2xs"
          size="medium"
          variant="secondary"
          :loading="sending"
          @click="emit('send')"
        >
          {{ sent ? t('receipt-model.billing_letter.resend') : t('receipt-model.billing_letter.send') }}
        </SolButton>
      </div>

      <template v-if="sent">
        <SolDivider thickness="x-small" orientation="horizontal" />
        <BillingLetterNextSteps :supplier-name="supplierName" />
      </template>
    </template>
  </section>
</template>

<style lang="scss" scoped>
// Value without a token: title 18px.
.billing-letter {
  @apply flex flex-col gap-4xs p-2xs rounded-md bg-neutral-high-light;

  &__header {
    @apply flex flex-col gap-quark;
  }

  &__title {
    @apply m-0 font-highlight font-regular text-[18px] leading-xs text-neutral-low-dark;
  }

  &__description {
    @apply m-0 fonts-body-large-regular text-neutral-low-light;

    line-height: theme('lineHeight.lg');
  }

  &__sent-alert {
    @apply flex-col items-start gap-4xs;

    @screen md:site {
      @apply flex-row items-center gap-0;
    }
  }

  &__send-button {
    @apply flex-shrink-0 whitespace-nowrap;
  }
}
</style>
